import { channelKey } from './light_profiles';

// The phase stores only selected settings. This projection is for the shared
// editor and previews; it is never used as a controller catalog submission.
export const emptyPhaseLighting = () => ({
  schema_version: 1,
  mode: 0,
  master_override_mask: 0,
  enabled_override_mask: 0,
  enabled_mask: 0,
  zone_master: 100,
  master: [100, 100, 100, 100],
  channels: []
});

const phaseValues = (lighting) =>
  lighting?.schema_version === 1 ? lighting : emptyPhaseLighting();
const channelOverride = (lighting, channel) =>
  lighting.channels.find(
    (entry) => entry.subzone === channel.subzone && entry.channel === channel.channel
  );
const scopeBit = (field, identity) => 1 << (field.startsWith('zone_') ? 4 : identity.subzone);

export function phaseLightingSelected(lighting, field, identity) {
  if (!['mode', 'zone_master', 'zone_enabled', 'subzone_master', 'subzone_enabled',
    'channel_maximum', 'channel_enabled', 'channel_profile'].includes(field)) return false;
  const phase = phaseValues(lighting);
  if (field === 'mode') return phase.mode === 1 || phase.mode === 2;
  if (field === 'zone_enabled' && phase.mode === 3) return true;
  if (field.startsWith('channel_')) {
    const channel = channelOverride(phase, identity);
    if (field === 'channel_maximum') return Boolean(channel?.maximum_override);
    if (field === 'channel_enabled')
      return Boolean(channel?.enable_override || channel?.kind === 2);
    return channel?.kind === 1;
  }
  const mask = field.endsWith('_master') ? phase.master_override_mask : phase.enabled_override_mask;
  return Boolean(mask & scopeBit(field, identity));
}

export function phaseLightingCatalog(catalog, zone, lighting) {
  const phase = phaseValues(lighting);
  const channels = catalog.channels.map((channel) => {
    if (channel.zone !== zone) return channel;
    const selected = channelOverride(phase, channel);
    return {
      ...channel,
      maximum: selected?.maximum_override ? selected.maximum : channel.maximum,
      points: selected?.kind === 1 ? selected.points : channel.points,
      _profile_off: selected?.kind === 2
    };
  });
  return {
    ...catalog,
    advanced_zone_mask:
      phase.mode === 1
        ? catalog.advanced_zone_mask & ~(1 << zone)
        : phase.mode === 2
          ? catalog.advanced_zone_mask | (1 << zone)
          : catalog.advanced_zone_mask,
    zones: catalog.zones.map((entry) =>
      entry.zone !== zone
        ? entry
        : {
            ...entry,
            master: phase.master_override_mask & 16 ? phase.zone_master : entry.master,
            enabled:
              phase.mode === 3
                ? 0
                : phase.enabled_override_mask & 16
                  ? Number(Boolean(phase.enabled_mask & 16))
                  : entry.enabled
          }
    ),
    subzones: catalog.subzones.map((entry) => {
      if (entry.zone !== zone) return entry;
      const bit = 1 << entry.subzone;
      let enabledMask = entry.enabled_mask;
      phase.channels
        .filter((channel) => channel.subzone === entry.subzone)
        .forEach((channel) => {
          if (channel.kind === 2 || channel.enable_override) {
            enabledMask =
              channel.kind !== 2 && channel.enabled
                ? enabledMask | (1 << channel.channel)
                : enabledMask & ~(1 << channel.channel);
          }
        });
      return {
        ...entry,
        master: phase.master_override_mask & bit ? phase.master[entry.subzone] : entry.master,
        enabled:
          phase.enabled_override_mask & bit
            ? Number(Boolean(phase.enabled_mask & bit))
            : entry.enabled,
        enabled_mask: enabledMask
      };
    }),
    channels
  };
}

// This is the phase adapter's explicit field edit, not a catalog diff or a
// migration. Selecting a field snapshots the currently displayed value; clearing
// it resumes ordinary settings. Existing schedule controls persist the result.
export function changePhaseLighting(lighting, field, identity, value, selected = true) {
  const phase = phaseValues(lighting);
  const next = { ...phase };
  if (field === 'mode') {
    next.mode = selected ? (value ? 2 : 1) : 0;
    // Changing how the curve is chosen must not implicitly enable an Off phase.
    if (phase.mode === 3) {
      next.enabled_override_mask |= 16;
      next.enabled_mask &= ~16;
    }
  } else if (field.startsWith('channel_')) {
    const previous = channelOverride(phase, identity);
    const channel = {
      subzone: identity.subzone,
      channel: identity.channel,
      kind: 0,
      maximum_override: false,
      maximum: identity.maximum,
      enable_override: false,
      enabled: true,
      points: [],
      ...previous
    };
    if (field === 'channel_maximum') {
      channel.maximum_override = selected;
      if (selected) channel.maximum = value;
    } else if (field === 'channel_enabled') {
      channel.enable_override = selected;
      if (selected) channel.enabled = Boolean(value);
      if (channel.kind === 2) channel.kind = 0;
    } else {
      if (channel.kind === 2) return phase;
      channel.kind = selected ? 1 : 0;
      channel.points = selected ? value : [];
    }
    next.channels = phase.channels.filter(
      (entry) => entry.subzone !== channel.subzone || entry.channel !== channel.channel
    );
    if (channel.kind || channel.maximum_override || channel.enable_override)
      next.channels.push(channel);
  } else {
    const bit = scopeBit(field, identity);
    if (field.endsWith('_master')) {
      next.master_override_mask = selected
        ? phase.master_override_mask | bit
        : phase.master_override_mask & ~bit;
      if (selected) {
        if (field === 'zone_master') next.zone_master = value;
        else
          next.master = phase.master.map((master, index) =>
            index === identity.subzone ? value : master
          );
      }
    } else {
      next.enabled_override_mask = selected
        ? phase.enabled_override_mask | bit
        : phase.enabled_override_mask & ~bit;
      if (selected)
        next.enabled_mask = value ? phase.enabled_mask | bit : phase.enabled_mask & ~bit;
      if (field === 'zone_enabled' && phase.mode === 3) next.mode = 0;
    }
  }
  return next;
}

export function phaseLightingValue(catalog, zone, field, identity) {
  if (field === 'mode') return Boolean(catalog.advanced_zone_mask & (1 << zone));
  if (field.startsWith('zone_'))
    return catalog.zones.find((entry) => entry.zone === zone)[field.slice(5)];
  const subzone = catalog.subzones.find(
    (entry) => entry.zone === zone && entry.subzone === identity.subzone
  );
  if (field.startsWith('subzone_')) return subzone[field.slice(8)];
  if (field === 'channel_enabled') return Boolean(subzone.enabled_mask & (1 << identity.channel));
  const channel = catalog.channels.find((entry) => channelKey(entry) === channelKey(identity));
  return field === 'channel_maximum' ? channel.maximum : channel.points;
}
